import React, { useState, useEffect } from 'react'
import { AnnouncementFormModalMetaProps, Announcement } from '../data/types'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'

type Props = {
    meta: AnnouncementFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (announcementData: any) => Promise<void>
    initialAnnouncement?: Announcement | null
}

export const AnnouncementFormModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, initialAnnouncement } = props

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        priority: false
    })

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isEditEnabled, setIsEditEnabled] = useState(false);


    // Populate form when editing an event
    useEffect(() => {
        if (initialAnnouncement && isOpen) {
            setFormData({
                title: initialAnnouncement.title,
                content: initialAnnouncement.content || '',
                priority: initialAnnouncement.priority
            });
            setIsEditEnabled(false); // lock fields while viewing
        }
        else if (!isOpen) {
            setFormData({ title: '', content: '', priority: false });
            setError(null);
            setIsEditEnabled(true); // new form defaults to editable
        }
    }, [initialAnnouncement, isOpen]);



    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            await onSubmit(formData)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={initialAnnouncement ? meta.editTitle : meta.createTitle}
            maxWidth="2xl"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        variant="outline"
                        color="neutral"
                    >
                        {meta.cancelButton}
                    </Button>

                    {isEditEnabled && (
                        <Button
                            type="submit"
                            form="announcement-form"
                            disabled={loading}
                        >
                            {loading ? meta.submittingButton : meta.submitButton}
                        </Button>
                    )}
                </div>
            }

        >
            <form id="announcement-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    {initialAnnouncement && (
                        <div className="flex items-center gap-3 mb-4">
                            <label
                                htmlFor="editToggle"
                                className="text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                Enable Editing
                            </label>

                            <button
                                type="button"
                                onClick={() => setIsEditEnabled(prev => !prev)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors 
                                ${isEditEnabled ? "bg-primary-500" : "bg-gray-300 dark:bg-gray-600"}`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform
                    ${isEditEnabled ? "translate-x-5" : "translate-x-1"}`}
                                />
                            </button>
                        </div>
                    )}



                    <div className="grid grid-cols-1 gap-4">
                        {/* Announcement Title */}
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.titleLabel} <span className="text-danger-500">{isEditEnabled ? '*' : ''}</span>
                            </label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                required
                                disabled={!isEditEnabled}
                                value={formData.title}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                                focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.titlePlaceHolder}
                            />

                        </div>

                        {/* Announcement Content Text Box */}
                        <div>
                            <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {meta.contentLabel || "Content"} <span className="text-danger-500">{isEditEnabled ? '*' : ''}</span>
                            </label>
                            <textarea
                                id="content"
                                name="content"
                                disabled={!isEditEnabled}
                                value={formData.content}
                                onChange={handleChange}
                                required
                                rows={6}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm 
                                focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder={meta.contentPlaceHolder || "Write announcement details here..."}
                            />

                        </div>

                        {/* Priority Toggler */}
                        <div className="flex items-center gap-3">
                            <label htmlFor="priority" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Mark as Priority
                            </label>

                            <input
                                id="priority"
                                name="priority"
                                type="checkbox"
                                disabled={!isEditEnabled}
                                checked={formData.priority}
                                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.checked }))}
                                className="h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 dark:bg-gray-700"
                            />

                        </div>
                    </div>

                </div>
            </form>
        </Modal >
    )
}
